import { memo, useState } from 'react';
import { Calculator, Settings, TrendingUp, DollarSign, Users, Target, Phone, ShoppingCart } from 'lucide-react';
import { ProjectionsModal } from './ProjectionsModal';
import MetricCard from '../../common/MetricCard';

export const Projections = memo(({ inputs, results, updateInput, resetInputs }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!results) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Projected Funnel Metrics */}
      <div>
        
        {/* Header with Settings Button */}
        <div className="flex items-center justify-between">
            <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Projected Funnel</h4>
            </div>
            <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
            <Settings className="w-4 h-4 bg-blue-600" />
            </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard
            title="Visitors"
            value={results.visitors.toLocaleString()}
            icon={Users}
            subtitle="Monthly traffic"
          />
          
          <MetricCard
            title="MQLs"
            value={results.mqls.toLocaleString()}
            icon={Target}
            subtitle="Marketing qualified"
          />
          
          <MetricCard
            title="SQLs"
            value={results.sqls.toLocaleString()}
            icon={Phone}
            subtitle="Sales qualified"
          />
          
          <MetricCard
            title="Booked"
            value={results.showUps.toLocaleString()}
            icon={Users}
            subtitle="Show ups"
          />
          
          <MetricCard
            title="Closed Deals"
            value={results.closes.toLocaleString()}
            icon={ShoppingCart}
            subtitle="Won opportunities"
          />
        </div>
      </div>

      {/* Financial Projections */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Financial Projections</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Projected Revenue</p>
                <p className="text-3xl font-bold text-green-700 mt-2">
                  ${results.revenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">From {results.closes} deals</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Marketing Cost</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  ${results.totalCost.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total spend</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-sm border ${
            results.roi > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ROI</p>
                <p className={`text-3xl font-bold mt-2 ${
                  results.roi > 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {results.roi.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Return on investment</p>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-sm border ${
            results.roas > 1 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ROAS</p>
                <p className={`text-3xl font-bold mt-2 ${
                  results.roas > 1 ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {results.roas.toFixed(2)}x
                </p>
                <p className="text-xs text-gray-500 mt-1">Return on ad spend</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm opacity-90">Projected Monthly Performance</p>
            <p className="text-2xl font-bold mt-1">
              {results.closes} deals worth ${results.revenue.toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">Efficiency Metrics</p>
            <p className="text-xl font-semibold mt-1">
              {results.roas.toFixed(2)}x ROAS • {results.roi.toFixed(0)}% ROI • ${results.cpa.toFixed(0)} CPA
            </p>
          </div>
        </div>
      </div>

      {/* Modal */}
      <ProjectionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        inputs={inputs}
        updateInput={updateInput}
        resetInputs={resetInputs}
      />
    </div>
  );
});

Projections.displayName = 'Projections';